import Lightbox from 'yet-another-react-lightbox';
import { FileWithPreview, formatBytes, useFileUpload } from '@/features/_shared/hooks/use-file-upload';
import { Button } from '@/components/_shadcn-ui/button';
import { useState, useCallback } from 'react';
import { TFileItem } from '../../types/upload-file.type';
import { AlertCircleIcon, DownloadIcon, XIcon, ImageOffIcon } from 'lucide-react';
import FileIcon from './get-file-icon';
import { checkIsImageFile, downloadFile } from '../../utils/file-handler';
import { handleDomBubbleEvent } from '@/utils/dom';
import { cn } from '@/utils/cn';

export interface IFileUploadProps {
  disabled?: boolean;
  initialFiles: TFileItem[];
  isUploadingFiles?: boolean;
  uploadingFileIds?: string[]; // Array of file IDs that are currently uploading
  maxSize?: number;
  maxFiles?: number;
  accept?: string; // Comma-separated file types (e.g., '.pdf,.doc' or 'image/*' or '*')
  onFilesChange?: (files: FileWithPreview[]) => void;
  onFilesAdded?: (files: FileWithPreview[]) => void;
  onFileRemoved?: (removedFile: FileWithPreview) => void;
}
// Helper function to format accept prop into readable text
const formatAcceptTypes = (accept?: string): string => {
  if (!accept || accept === '*') {
    return 'All files';
  }

  const types = accept.split(',').map((type) => type.trim());

  // If there are many types, show a summary
  if (types.length > 5) {
    const fileExtensions = types
      .filter((type) => type.startsWith('.'))
      .map((type) => type.substring(1).toUpperCase())
      .slice(0, 5)
      .join(', ');
    return `${fileExtensions}... (${types.length} types)`;
  }

  // Format each type
  const formattedTypes = types.map((type) => {
    if (type.startsWith('.')) {
      // File extension like .pdf, .doc
      return type.substring(1).toUpperCase();
    }
    if (type.endsWith('/*')) {
      // MIME type pattern like image/*, application/*
      const category = type.split('/')[0];
      return `${category.charAt(0).toUpperCase() + category.slice(1)} files`;
    }
    // For specific MIME types like application/pdf, image/png
    if (type.includes('/')) {
      const parts = type.split('/');
      if (parts.length === 2 && parts[1] !== '*') {
        // Common file types that are well-known
        const commonTypes: Record<string, string> = {
          pdf: 'PDF files',
          zip: 'ZIP files',
          doc: 'DOC files',
          docx: 'DOCX files',
          xls: 'XLS files',
          xlsx: 'XLSX files',
          ppt: 'PPT files',
          pptx: 'PPTX files',
          png: 'PNG files',
          jpg: 'JPG files',
          jpeg: 'JPEG files',
          gif: 'GIF files',
          svg: 'SVG files',
          txt: 'TXT files',
          csv: 'CSV files',
        };
        const lowerType = parts[1].toLowerCase();
        if (commonTypes[lowerType]) {
          return commonTypes[lowerType];
        }
        // For less common types, show the extension
        return `${parts[1].toUpperCase()} files`;
      }
      return parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
    }
    return type;
  });

  return formattedTypes.join(', ');
};

// Helper function to truncate filename while preserving extension
const truncateFileName = (fileName: string, maxLength: number = 80): string => {
  if (fileName.length <= maxLength) return fileName;

  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex === -1 || lastDotIndex === 0) {
    // No extension or hidden file starting with dot
    return `${fileName.substring(0, maxLength - 3)}...`;
  }

  const extension = fileName.substring(lastDotIndex);
  const baseName = fileName.substring(0, lastDotIndex);
  const maxBaseLength = maxLength - extension.length - 3; // 3 for "..."

  if (maxBaseLength <= 0) {
    return fileName.substring(0, maxLength - 3) + '...';
  }

  return `${baseName.substring(0, maxBaseLength)}...${extension}`;
};

export default function NewFileUpload({
  disabled = false,
  initialFiles,
  isUploadingFiles: _isUploadingFiles = false,
  uploadingFileIds = [],
  maxSize = 100 * 1024 * 1024,
  maxFiles = 10,
  accept = '*',
  onFilesChange,
  onFilesAdded,
  onFileRemoved,
}: IFileUploadProps) {
  const [selectedPreview, setSelectedPreview] = useState<string | null>(null);
  const [brokenImages, setBrokenImages] = useState<Set<string>>(new Set());

  const handleImageError = useCallback((fileId: string) => {
    setBrokenImages((prev) => new Set(prev).add(fileId));
  }, []);

  const [
    { files, isDragging, errors },
    {
      handleDragEnter,
      handleDragLeave,
      handleDragOver,
      handleDrop,
      openFileDialog,
      removeFile,
      clearFiles: _clearFiles,
      getInputProps,
    },
  ] = useFileUpload({
    multiple: true,
    maxFiles,
    maxSize,
    accept,
    initialFiles:
      initialFiles?.length > 0
        ? initialFiles.map((file) => ({
            name: file.filename,
            size: file.filesize,
            type: file.mimetype,
            url: file.url,
            id: file.key,
          }))
        : [],
    onFilesChange: (files) => {
      onFilesChange?.(files);
    },
    onFilesAdded: (files) => {
      onFilesAdded?.(files);
    },
    onFileRemoved: (removedFile) => {
      onFileRemoved?.(removedFile);
    },
  });

  return (
    <div className="flex flex-col gap-2">
      {/* Drop area */}
      <div
        role="button"
        onClick={(e) => {
          if (disabled) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          e.stopPropagation();
          openFileDialog();
        }}
        onDragEnter={(e) => {
          if (disabled) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          handleDragEnter(e);
        }}
        onDragLeave={(e) => {
          if (disabled) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          handleDragLeave(e);
        }}
        onDragOver={(e) => {
          if (disabled) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          handleDragOver(e);
        }}
        onDrop={(e) => {
          if (disabled) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          handleDrop(e);
        }}
        data-dragging={isDragging || undefined}
        className={cn(
          'flex min-h-40 flex-col items-center justify-center rounded-xl border border-dashed border-[#D9D9D9] p-4 transition-colors has-[input:focus]:border-ring has-[input:focus]:ring-[3px] has-[input:focus]:ring-ring/50 data-[dragging=true]:bg-[#0C8DBF]/50',
          {
            'opacity-50 cursor-not-allowed': disabled,
            'hover:bg-[#0C8DBF]/10 cursor-pointer': !disabled,
          }
        )}
      >
        <input {...getInputProps()} disabled={disabled} className="sr-only" aria-label="Upload files" />

        <div className="flex flex-col items-center justify-center text-center">
          <div className="mb-2 flex size-11 shrink-0 items-center justify-center " aria-hidden="true">
            {/* <FileUpIcon className="size-4 opacity-100" /> */}
            <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none">
              <path
                d="M11.1583 22.75H5.41667C4.84203 22.75 4.29093 22.5217 3.8846 22.1154C3.47827 21.7091 3.25 21.158 3.25 20.5833V5.41667C3.25 4.84203 3.47827 4.29093 3.8846 3.8846C4.29093 3.47827 4.84203 3.25 5.41667 3.25H20.5833C21.158 3.25 21.7091 3.47827 22.1154 3.8846C22.5217 4.29093 22.75 4.84203 22.75 5.41667V16.25L19.3917 12.8917C18.984 12.4921 18.4351 12.2696 17.8643 12.2724C17.2935 12.2753 16.7468 12.5032 16.3432 12.9068L6.5 22.75"
                stroke="#0C8DBF"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M15.168 21.125L18.418 17.875L21.668 21.125"
                stroke="#0C8DBF"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M18.418 23.8333V17.875"
                stroke="#0C8DBF"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9.7487 11.9154C10.9453 11.9154 11.9154 10.9453 11.9154 9.7487C11.9154 8.55208 10.9453 7.58203 9.7487 7.58203C8.55208 7.58203 7.58203 8.55208 7.58203 9.7487C7.58203 10.9453 8.55208 11.9154 9.7487 11.9154Z"
                stroke="#0C8DBF"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p className="mb-2 text-[12px] flex items-center gap-[4px] font-[400] text-[#9D9D9C]">
            <span className="text-[#0C8DBF]">Click to upload</span>
            <span>or drag and drop</span>
          </p>
          <div className="flex flex-wrap justify-center gap-1 text-xs text-muted-foreground/70">
            <span>{formatAcceptTypes(accept)}</span>
            <span>∙</span>
            <span>Max {maxFiles} files</span>
            <span>∙</span>
            <span>Up to {formatBytes(maxSize)}</span>
          </div>
        </div>
      </div>

      {errors.length > 0 && (
        <div className="flex items-center gap-1 text-xs text-destructive" role="alert">
          <AlertCircleIcon className="size-3 shrink-0" />
          <span>{errors[0]}</span>
        </div>
      )}

      {/* File list */}
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((file) => (
            <div
              onClick={(e) => {
                handleDomBubbleEvent(e);
                setSelectedPreview(file.preview || null);
              }}
              key={file.id}
              className="flex cursor-pointer items-center justify-between gap-2 rounded-lg border bg-background p-2 pe-3"
            >
              <div className="flex items-center gap-3 overflow-hidden">
                <div className="flex aspect-square size-10 shrink-0 items-center justify-center rounded border overflow-hidden">
                  {file.preview && checkIsImageFile(file) && !brokenImages.has(file.id) ? (
                    <img
                      src={file.preview}
                      alt={file.file instanceof File ? file.file.name : file.file.name}
                      className="w-full h-full object-cover"
                      onError={() => handleImageError(file.id)}
                    />
                  ) : brokenImages.has(file.id) ? (
                    <div className="flex items-center justify-center w-full h-full bg-muted">
                      <ImageOffIcon className="size-5 text-muted-foreground" />
                    </div>
                  ) : (
                    <FileIcon file={file} />
                  )}
                </div>
                <div className="flex min-w-0 flex-col gap-0.5">
                  {uploadingFileIds.includes(file.id) ? (
                    <p className="truncate text-[13px] font-medium">Uploading...</p>
                  ) : (
                    <p className="truncate text-[13px] font-medium">
                      {truncateFileName(file.file instanceof File ? file.file.name : file.file.name)}
                    </p>
                  )}
                  {uploadingFileIds.includes(file.id) ? (
                    <p className="text-xs text-muted-foreground">Uploading...</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {formatBytes(file.file instanceof File ? file.file.size : file.file.size)}
                    </p>
                  )}
                </div>
              </div>

              {!disabled && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="-me-2 size-8 text-muted-foreground/80 hover:bg-transparent hover:text-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    removeFile(file.id);
                  }}
                  aria-label="Remove file"
                >
                  <XIcon className="size-4" aria-hidden="true" />
                </Button>
              )}
            </div>
          ))}

          {/* Remove all files button */}
          {/* {files.length > 1 && (
            <div>
              <Button size="sm" variant="outline" onClick={clearFiles}>
                Remove all files
              </Button>
            </div>
          )} */}
        </div>
      )}

      <Lightbox
        styles={{
          root: { '--yarl__color_backdrop': 'rgba(0, 0, 0, .8)' },
        }}
        open={!!selectedPreview}
        close={() => {
          setSelectedPreview(null);
        }}
        slides={[{ src: selectedPreview || '' }]}
        carousel={{ finite: true }}
        controller={{ closeOnBackdropClick: true }}
        render={{
          slide: ({ slide }) => {
            const currentFile = files.find((f) => f.preview === selectedPreview);
            const fileName = currentFile?.file instanceof File ? currentFile.file.name : currentFile?.file.name;
            const fileSize = currentFile?.file instanceof File ? currentFile.file.size : currentFile?.file.size;

            if (currentFile && checkIsImageFile(currentFile)) {
              // Show image for image files
              const isBroken = brokenImages.has(currentFile.id);
              return (
                <div className="flex flex-col items-center justify-center h-full">
                  {isBroken ? (
                    <div className="flex flex-col items-center justify-center">
                      <div className="mb-6 p-8 bg-black/60 rounded-full">
                        <ImageOffIcon className="size-16 text-white/60" />
                      </div>
                      <div className="text-center text-white">
                        <p className="text-lg font-medium mb-2">Image failed to load</p>
                        <p className="text-sm opacity-75">{fileName}</p>
                        <p className="text-sm opacity-75">{formatBytes(fileSize || 0)}</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <img
                        src={slide.src}
                        alt={fileName}
                        className="max-h-[80vh] max-w-[80vw] object-contain"
                        onError={() => handleImageError(currentFile.id)}
                      />
                      <div className="mt-4 text-center text-white">
                        <p className="text-lg font-medium">{fileName}</p>
                        <p className="text-sm opacity-75">{formatBytes(fileSize || 0)}</p>
                      </div>
                    </>
                  )}
                </div>
              );
            }

            // Show icon and download button for non-image files
            if (!currentFile) {
              return null;
            }

            return (
              <div className="flex flex-col items-center justify-center h-full p-8">
                <div className="flex flex-col items-center justify-center bg-black/60 rounded-2xl p-12 max-w-md w-full">
                  <div className="mb-6 p-6 bg-gray-200 rounded-full">
                    <FileIcon file={currentFile} />
                  </div>
                  <div className="text-center text-white mb-6">
                    <p className="text-xl font-medium mb-2">{fileName}</p>
                    <p className="text-sm opacity-75">{formatBytes(fileSize || 0)}</p>
                  </div>
                  <Button
                    onClick={() => downloadFile(currentFile)}
                    className="bg-white text-black hover:bg-white/90 px-6 py-2 rounded-lg font-medium cursor-pointer"
                  >
                    <DownloadIcon className="size-4 mr-2" />
                    Download File
                  </Button>
                </div>
              </div>
            );
          },
          buttonNext: () => null,
          buttonPrev: () => null,
        }}
      />
    </div>
  );
}
